import { type ReactElement, type ReactNode, useMemo } from 'react';
import { type AnalyticsBiCatalogView, type AnalyticsBiFreshness, type AnalyticsBiQueryView } from '@vello/api-client';
import { type Granularity, type ResultCell } from '@vello/analytics-semantic';
import { Badge, BarList, Button, CheckRow, DateRangeInput, DonutChart, EmptyState, Field, FilterBar, LineChart, Notice, SegmentedControl, Select, StatCard, TextInput, type StatDelta, Stack, Text, ScrollX } from '@vello/web-theme';
import {
  BI_COMPARE_CHOICES,
  BI_MAX_LAST_DAYS,
  BI_MORE_PRESETS,
  BI_PRESETS,
  BI_UP_TO_NOW_BASELINES,
  type BiCompareChoice,
  type BiErrorView,
  type BiPeriod,
  type BiPeriodChoice,
  type BiUnit,
  type KpiFigure,
  biFormatter,
  biPeriodDays,
  computeDelta,
  customRangeError,
  deltaTone,
  dimensionValueText,
  editedPeriod,
  bucketText,
  freshnessAge,
  holidayMismatches,
  kpiFigures,
  narrowedMarkets,
  periodChoice,
  trendSeries,
} from '../../../src/index';
import { type CompareAgainst } from '@vello/analytics-semantic';
import { type CrossFilterTarget } from '../../../src/index';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { marketDate, marketReadableName, marketTimeZone, scopeTimeZone } from '../../../src/index';
import { useAdmin } from '../../session';
import { type Loadable } from './bi-hooks';

// ───────────────────────────── words ─────────────────────────────

/** A label that continues a sentence ("Compared with …", "vs …"): its first letter in lower case. */
const lowerFirst = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);

export interface BiText {
  readonly t: (id: string, vars?: Readonly<Record<string, string | number>>) => string;
  readonly lang: string;
  readonly fmt: ReturnType<typeof biFormatter>;
  readonly metricName: (id: string) => string;
  readonly metricHint: (id: string) => string;
  readonly dimName: (id: string) => string;
  /** The text of one dimension key (a month, a weekday, a code, a name). */
  readonly valueText: (dimensionId: string, key: string | null | undefined, label?: string | null, short?: boolean) => string;
  readonly marketName: (id: string) => string;
  /** D577: the central catalog — the label of a code in a vocabulary (`v('analytics.compare', code)`), in the console's language. */
  readonly v: (id: string, code: string | null | undefined, vars?: Readonly<Record<string, string | number>>) => string;
  /** D532: the name of a column of an export: a measure, a cut, its label or its comparison column; a catalogue report's column keeps the file's own name. */
  readonly columnName: (id: string) => string;
}

/** The words and figures of Análises: the catalog’s labels in the console’s language, codes humanized, never a raw id. */
export function useBiText(catalog?: AnalyticsBiCatalogView): BiText {
  const { t, lang } = useT();
  const { v, has } = useVocab();
  const { markets } = useAdmin();
  return useMemo(() => {
    const dims = new Map((catalog?.dimensions ?? []).map((d) => [d.id, d]));
    const fmt = biFormatter(lang);
    // D577: a code-typed dimension value reads from the catalog — the BI values first, then the cancel reasons / payment methods the cuts carry.
    const codeLabel = (code: string): string => {
      const lower = code.toLowerCase();
      for (const id of ['analytics.dimension_value', 'ride.cancel_reason', 'food.cancel_reason', 'payment.method']) {
        if (has(id, lower)) return v(id, lower);
        if (has(id, code)) return v(id, code);
      }
      return v('analytics.dimension_value', code);
    };
    return {
      t,
      v,
      lang,
      fmt,
      metricName: (id) => v('analytics.bi_metric', id),
      metricHint: (id) => (has('analytics.bi_metric_description', id) ? v('analytics.bi_metric_description', id) : ''),
      dimName: (id) => v('analytics.bi_dimension', id),
      valueText: (dimensionId, key, label, short) => {
        // D531: a bucket of a distribution reads as its range, a cohort age as months since the first order
        const bucket = bucketText(dimensionId, key, { duration: (s) => fmt.chartText('duration_s', s, undefined), signed: (s) => fmt.chartText('delta_s', s, undefined) });
        if (bucket !== undefined) return bucket;
        if (dimensionId === 'cohort_age' && key !== null && key !== undefined && /^\d{1,3}$/.test(key)) return `M${Number(key)}`;
        const type = dims.get(dimensionId)?.valueType ?? (['day', 'month', 'hour', 'year', 'quarter'].includes(dimensionId) ? dimensionId : 'ref');
        // F3 (ADR-0124): a state reads as its registered name - the answer's own label, else the name the caller's reach carries, else the code
        const named = type === 'region' && (label === null || label === undefined || label === '') && key !== null && key !== undefined ? catalog?.markets.find((m) => m.region === key)?.regionName : undefined;
        // D854 (L7a-006): a market the answer did not name reads as its registered name (the caller's reach, then the console's registry), never as a raw `cc:slug` id.
        const marketNamed = type === 'market' && key !== null && key !== undefined && key !== '' && (label === null || label === undefined || label === '' || label === key) ? (catalog?.markets.find((m) => m.id === key)?.name ?? marketReadableName(key, markets)) : undefined;
        return dimensionValueText(type, key, marketNamed ?? named ?? label, lang, codeLabel, t('bi.val.empty'), short === true);
      },
      // The catalog carries the names of the markets in the caller's reach (D523): no second lookup; the console's registry is the fallback.
      marketName: (id) => catalog?.markets.find((m) => m.id === id)?.name ?? marketReadableName(id, markets),
      columnName: (id) => {
        const known = (base: string): string | undefined => {
          if (has('analytics.bi_metric', base)) return v('analytics.bi_metric', base);
          return has('analytics.bi_dimension', base) ? v('analytics.bi_dimension', base) : undefined;
        };
        if (id === 'market_id') return v('analytics.bi_dimension', 'market');
        const own = known(id);
        if (own !== undefined) return own;
        if (id.endsWith('_label')) {
          const base = known(id.slice(0, -'_label'.length));
          if (base !== undefined) return `${base} · ${t('bi.exp.col.label')}`;
        }
        if (id.endsWith('_baseline')) {
          const base = known(id.slice(0, -'_baseline'.length));
          if (base !== undefined) return `${base} · ${t('bi.exp.col.baseline')}`;
        }
        return v('analytics.bi_metric', id);
      },
    };
  }, [t, lang, catalog, markets]);
}

// ───────────────────────────── controls ─────────────────────────────

export function PeriodControls({ period, onChange, idPrefix }: { period: BiPeriod; onChange: (p: BiPeriod) => void; idPrefix: string }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const { scope, markets } = useAdmin();
  const choice = periodChoice(period);
  // The always-visible dates: the days the period covers in the operating market's own zone (the server resolves a relative
  // period the same way), or the typed range. A preset or "last N days" FILLS them; editing one makes an explicit range.
  const today = marketDate(new Date(), scope.current.region !== undefined && scope.current.city === undefined ? scopeTimeZone(scope.current, markets) : marketTimeZone(scope.current.city, markets));
  const shown = biPeriodDays(period, today) ?? { from: '', to: '' };
  const problem = period.kind === 'custom' ? customRangeError(period.from, period.to) : undefined;
  const set = (c: BiPeriodChoice): void => onChange(c === 'custom' ? { kind: 'custom', from: shown.from, to: shown.to } : c === 'last_days' ? { kind: 'lastDays', days: period.kind === 'lastDays' ? period.days : 14 } : { kind: 'preset', preset: c });
  const days = period.kind === 'lastDays' ? period.days : 14;
  const more = (BI_MORE_PRESETS as readonly string[]).includes(choice) || choice === 'last_days' ? choice : '';
  // Only a quick preset reads as pressed; a "more" period shows in its select, a typed range leaves everything unpressed.
  const pressed = (BI_PRESETS as readonly string[]).includes(choice) ? (choice as BiPeriodChoice) : undefined;
  return (
    <Stack gap={2}>
      <FilterBar>
        {/* Five presets do not fit a phone: the control keeps its width and scrolls inside its own box, never the page. */}
        <ScrollX wide>
          <SegmentedControl<BiPeriodChoice>
            ariaLabel={t('bi.period')}
            value={pressed}
            onChange={set}
            options={BI_PRESETS.map((p) => ({ id: p, label: v('analytics.relative_period', p) }))}
          />
        </ScrollX>
        <DateRangeInput
          idPrefix={idPrefix}
          from={shown.from}
          to={shown.to}
          onFromChange={(d) => onChange(editedPeriod(period, today, 'from', d))}
          onToChange={(d) => onChange(editedPeriod(period, today, 'to', d))}
          fromLabel={t('bi.period.from')}
          toLabel={t('bi.period.to')}
          invalid={problem !== undefined}
        />
        {/* D528: every relative period the server knows (weeks run Monday to Sunday), and the last N days. */}
        <Field label={t('bi.period.more')} htmlFor={`${idPrefix}-more`}>
          <Select id={`${idPrefix}-more`} value={more} onChange={(e) => e.target.value !== '' && set(e.target.value as BiPeriodChoice)}>
            <option value="">{t('bi.period.moreChoose')}</option>
            {BI_MORE_PRESETS.map((p) => (
              <option key={p} value={p}>
                {v('analytics.relative_period', p)}
              </option>
            ))}
            <option value="last_days">{v('analytics.relative_period', 'last_days')}</option>
          </Select>
        </Field>
        {period.kind === 'lastDays' && (
          <Field label={t('bi.period.days')} htmlFor={`${idPrefix}-days`} {...(!Number.isInteger(days) || days < 1 || days > BI_MAX_LAST_DAYS ? { error: t('bi.period.daysInvalid', { max: BI_MAX_LAST_DAYS }) } : {})}>
            <TextInput id={`${idPrefix}-days`} type="number" min={1} max={BI_MAX_LAST_DAYS} value={String(days)} onChange={(e) => onChange({ kind: 'lastDays', days: Number(e.target.value) })} />
          </Field>
        )}
      </FilterBar>
      {problem === 'reversed' && <Text role="caption" tone="danger" block aria-live="polite">{t('bi.period.reversed')}</Text>}
    </Stack>
  );
}

export function CompareControls({ compare, upToNowAgainst, onChange, idPrefix }: { compare: BiCompareChoice; upToNowAgainst: CompareAgainst; onChange: (next: { compare: BiCompareChoice; upToNowAgainst: CompareAgainst }) => void; idPrefix: string }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  return (
    <FilterBar>
      <Field label={t('bi.compare')} htmlFor={`${idPrefix}-compare`}>
        <Select id={`${idPrefix}-compare`} value={compare} onChange={(e) => onChange({ compare: e.target.value as BiCompareChoice, upToNowAgainst })}>
          {BI_COMPARE_CHOICES.map((c) => (
            <option key={c} value={c}>
              {c === 'auto' || c === 'none' ? t(`bi.compare.${c}`) : v('analytics.compare', c)}
            </option>
          ))}
        </Select>
      </Field>
      {compare === 'up_to_now' && (
        <Field label={t('bi.compare.against')} htmlFor={`${idPrefix}-against`}>
          <Select id={`${idPrefix}-against`} value={upToNowAgainst} onChange={(e) => onChange({ compare, upToNowAgainst: e.target.value as CompareAgainst })}>
            {BI_UP_TO_NOW_BASELINES.map((c) => (
              <option key={c} value={c}>
                {v('analytics.compare', c)}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </FilterBar>
  );
}

export { CheckRow };

// ───────────────────────────── notices ─────────────────────────────

/** "Atualizado há 5 minutos": the age of the data, the warning tone when it is old (never silent). */
export function FreshnessBadge({ freshness }: { freshness: AnalyticsBiFreshness | undefined }): ReactElement | null {
  const { t, lang } = useT();
  if (freshness === undefined) return null;
  const age = freshnessAge(freshness);
  if (age === undefined) return <Badge tone="warning">{t('bi.fresh.none')}</Badge>;
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'always' });
  const when = rtf.format(-age.amount, age.unit);
  return <Badge tone={age.stale ? 'warning' : 'neutral'}>{t('bi.fresh', { when })}</Badge>;
}

/** What the answer says about itself: the narrowed scope, the comparison used, the holidays that make it unfair. */
export function AnswerNotes({
  view,
  fallback,
  expectedMarkets,
  text,
}: {
  view: AnalyticsBiQueryView;
  fallback: boolean;
  expectedMarkets: readonly string[];
  text: BiText;
}): ReactElement | null {
  const { t, v, lang } = text;
  const left = narrowedMarkets(expectedMarkets, view.scope.markets);
  const holidays = holidayMismatches(view.holidays);
  const dateFmt = new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  const compared = view.comparison;
  const reading = view.scope.markets.map(text.marketName).join(', ');
  return (
    <Stack gap={2}>
      {reading !== '' && <Text as="p" role="caption" tone="secondary">{t('bi.scope.reading', { markets: reading })}</Text>}
      {compared !== undefined && (
        <Text as="p" role="caption" tone="secondary">
          {t('bi.compare.caption', { kind: lowerFirst(v('analytics.compare', compared.kind)), from: dateFmt.format(new Date(`${compared.baselineEnvelope.from}T12:00:00Z`)), to: dateFmt.format(new Date(`${compared.baselineEnvelope.to}T12:00:00Z`)) })}
        </Text>
      )}
      {fallback && <Notice tone="neutral">{t('bi.compare.fallback')}</Notice>}
      {left.length > 0 && <Notice tone="warning">{t('bi.scope.narrowed', { markets: reading || t('bi.scope.none') })}</Notice>}
      {holidays.length > 0 && (
        <Notice tone="warning">
          {t('bi.holiday.warning')}{' '}
          {holidays.map((h) => `${text.marketName(h.marketId)}: ${dateFmt.format(new Date(`${h.date}T12:00:00Z`))}${h.name !== null ? ` (${h.name})` : ''} — ${t(`bi.holiday.${h.side}`)}`).join('; ')}
        </Notice>
      )}
    </Stack>
  );
}

/** A refused or failed read in plain words, with the retry when waiting could help and the reference for support. */
export function BiProblem({ error, onRetry, text }: { error: BiErrorView; onRetry?: () => void; text: BiText }): ReactElement {
  const { t } = text;
  return (
    <Stack gap={2} align="start" role="alert">
      <Notice tone={error.kind === 'forbidden' || error.kind === 'market_forbidden' || error.kind === 'personal_forbidden' || error.kind === 'linkage_forbidden' ? 'neutral' : 'warning'}>{t(error.messageId)}</Notice>
      <Text role="caption" tone="secondary">{t('bi.err.reference', { code: error.reference })}</Text>
      {error.retryable && onRetry !== undefined && (
        <Button variant="ghost" icon="rotateCcw" onClick={onRetry}>
          {t('ui.retry')}
        </Button>
      )}
    </Stack>
  );
}

// ───────────────────────────── KPI cards ─────────────────────────────

interface KpiMetric {
  readonly id: string;
  readonly unit: BiUnit;
}

function deltaProps(text: BiText, metricId: string, unit: BiUnit, figure: KpiFigure, comparisonLabel: string | undefined): StatDelta | undefined {
  const base = figure.baseline?.[metricId];
  if (base === undefined) return undefined;
  const d = computeDelta(unit, figure.values[metricId], base);
  if (d.kind === 'none') return undefined;
  const shown = text.fmt.delta(d, text.t('bi.pp'));
  const tone = deltaTone(metricId, d);
  return {
    text: shown ?? text.t('bi.delta.new'),
    direction: d.direction,
    ...(tone !== 'neutral' ? { tone } : {}),
    ...(comparisonLabel !== undefined ? { srLabel: comparisonLabel } : {}),
  };
}

/** One card per measure (and per currency for money, which is never summed), with the comparison delta. */
export function KpiCards({ metrics, view, text }: { metrics: readonly string[]; view: AnalyticsBiQueryView; text: BiText }): ReactElement {
  const figures = kpiFigures(view);
  const units = new Map(view.metrics.map((m): [string, KpiMetric] => [m.id, { id: m.id, unit: m.unit as BiUnit }]));
  const comparisonLabel = view.comparison !== undefined ? text.t('bi.vs', { kind: lowerFirst(text.v('analytics.compare', view.comparison.kind)) }) : undefined;
  const cards: ReactElement[] = [];
  for (const id of metrics) {
    const unit = units.get(id)?.unit ?? 'count';
    const list = figures.length > 0 ? figures : [{ values: {} } as KpiFigure];
    for (const figure of list) {
      const cell: ResultCell | undefined = figure.values[id];
      const shown = text.fmt.cell(unit, cell, figure.currency);
      const label = figure.currency !== undefined ? `${text.metricName(id)} · ${figure.currency}` : text.metricName(id);
      const delta = deltaProps(text, id, unit, figure, comparisonLabel);
      const n = unit === 'count' ? (typeof cell?.value === 'number' ? cell.value : undefined) : undefined;
      cards.push(
        <StatCard
          key={`${id}:${figure.currency ?? ''}`}
          label={label}
          value={shown ?? text.t('ui.unavailable')}
          {...(shown === undefined ? { hint: text.t('bi.kpi.unavailable') } : {})}
          {...(n !== undefined ? { count: { value: n, format: (v: number) => text.fmt.count(v) } } : {})}
          {...(delta !== undefined ? { delta } : {})}
        />,
      );
    }
  }
  return <>{cards}</>;
}

export function KpiSkeletons({ metrics, text }: { metrics: readonly string[]; text: BiText }): ReactElement {
  return (
    <>
      {metrics.map((id) => (
        <StatCard key={id} label={text.metricName(id)} value="" loading loadingLabel={text.t('ui.loading')} />
      ))}
    </>
  );
}


// ───────────────────────────── charts ─────────────────────────────

/** The trend of one measure over the period, one line per currency, the comparison as a second line aligned on the same day or hour. */
export function TrendCharts({ view, grain, metric, title, text, loading }: { view: AnalyticsBiQueryView | undefined; grain: Granularity; metric: string; title: string; text: BiText; loading: boolean }): ReactElement {
  const { t, fmt } = text;
  if (view === undefined) {
    return <LineChart title={title} labels={[]} series={[]} loading={loading} loadingLabel={t('ui.loading')} emptyLabel={t('bi.chart.empty')} />;
  }
  const unit = (view.metrics.find((m) => m.id === metric)?.unit ?? 'count') as BiUnit;
  const series = trendSeries(view, grain, metric, (cell, currency) => fmt.chartValue(unit, cell, currency));
  if (series.length === 0) return <LineChart title={title} labels={[]} series={[]} emptyLabel={t('bi.chart.empty')} />;
  return (
    <Stack gap={4}>
      {series.map((s) => {
        const hasBase = s.points.some((p) => p.baseline !== undefined);
        const name = s.currency !== undefined ? `${title} · ${s.currency}` : title;
        return (
          <LineChart
            key={s.currency ?? 'all'}
            title={name}
            labels={s.points.map((p) => text.valueText(grain, p.key, undefined, true))}
            series={[
              { key: 'current', label: t('bi.series.current'), values: s.points.map((p) => p.current) },
              ...(hasBase ? [{ key: 'baseline', label: t('bi.series.baseline'), values: s.points.map((p) => p.baseline ?? null) }] : []),
            ]}
            formatValue={(n) => fmt.chartText(unit, n, s.currency)}
            emptyLabel={t('bi.chart.empty')}
            area={!hasBase}
            height={220}
          />
        );
      })}
    </Stack>
  );
}

/** A ranking of rows by one dimension: bars (one list per currency for money) or a donut for counts that are parts of a whole. */
export function BreakdownChart({
  view,
  metric,
  dimension,
  kind,
  title,
  text,
  crossFilter,
  selected,
}: {
  view: AnalyticsBiQueryView;
  metric: string;
  dimension: string;
  kind: 'bars' | 'donut';
  title: string;
  text: BiText;
  /** D528: a click on a mark filters the screen to its value (Shift adds). Absent = a plain picture. */
  crossFilter?: CrossFilterTarget;
  /** The values of this dimension the screen is filtered to, so their marks read as selected. */
  selected?: readonly string[];
}): ReactElement {
  const { t, fmt } = text;
  const raw = new Map<string, string | null>();
  const unit = (view.metrics.find((m) => m.id === metric)?.unit ?? 'count') as BiUnit;
  const groups = new Map<string, { key: string; label: string; value: number }[]>();
  for (const row of view.rows) {
    const cell = row.values[metric];
    if (cell === undefined) continue;
    const k = row.keys[dimension];
    const g = groups.get(row.currency ?? '') ?? [];
    const itemKey = `${k?.key ?? ''}|${row.marketId ?? ''}|${row.currency ?? ''}`;
    raw.set(itemKey, k?.key ?? null);
    g.push({ key: itemKey, label: text.valueText(dimension, k?.key, k?.label), value: fmt.chartValue(unit, cell, row.currency) });
    groups.set(row.currency ?? '', g);
  }
  if (groups.size === 0) return <EmptyState title={t('bi.chart.empty')} />;
  return (
    <Stack gap={4}>
      {[...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, items]) => {
        const name = currency !== '' ? `${title} · ${currency}` : title;
        const format = (n: number): string => fmt.chartText(unit, n, currency === '' ? undefined : currency);
        // Cross-filter (D528): the mark's own key (never its label) goes to the screen; a mark with no value (a missing reference) is not clickable.
        const pick = (key: string | undefined, how: { additive: boolean }): void => {
          const value = key === undefined ? null : raw.get(key);
          if (crossFilter !== undefined && value !== null && value !== undefined) crossFilter.onSelect(dimension, value, how.additive ? { additive: true } : {});
        };
        const selectedKeys = items.filter((i) => selected?.includes(raw.get(i.key) ?? '\u0000')).map((i) => i.key);
        const select = crossFilter === undefined ? {} : { selectedKeys };
        return kind === 'donut' && unit === 'count' ? (
          <DonutChart
            key={currency}
            title={name}
            slices={items.map((i) => ({ key: i.key, label: i.label, value: i.value }))}
            formatValue={format}
            formatShare={(f) => fmt.percent(f)}
            emptyLabel={t('bi.chart.empty')}
            size={160}
            {...select}
            {...(crossFilter !== undefined ? { onSelect: (s: { key?: string }, _i: number, how: { additive: boolean }) => pick(s.key, how) } : {})}
          />
        ) : (
          <BarList
            key={currency}
            title={name}
            items={items.map((i) => ({ key: i.key, label: i.label, value: i.value }))}
            formatValue={format}
            emptyLabel={t('bi.chart.empty')}
            {...select}
            {...(crossFilter !== undefined ? { onSelect: (item: { key?: string }, _i: number, how: { additive: boolean }) => pick(item.key, how) } : {})}
          />
        );
      })}
    </Stack>
  );
}

export type { Loadable, ReactNode };
